import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, XCircle, Loader2 } from "lucide-react";
import SEO from "../components/SEO";
import api from "../lib/api";

export function PaiementConfirme() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setFailed(true);
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/peloton/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setData(data);
          return;
        }
      } catch {
        /* retry */
      }
      tries += 1;
      if (tries < 8) {
        timer = setTimeout(poll, 2000);
      } else {
        setFailed(true);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-[75vh] bg-navy flex items-center justify-center px-5 py-32">
      <SEO title="Paiement confirmé" />
      <div className="bg-navy2 border border-white/10 p-8 sm:p-12 max-w-xl w-full text-center" data-testid="paiement-confirme">
        {!data && !failed && (
          <>
            <Loader2 className="animate-spin text-rouge mx-auto" size={36} />
            <p className="mt-6 text-white/70">Confirmation du paiement en cours…</p>
          </>
        )}
        {data && (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center bg-rouge text-white">
              <Check size={26} />
            </span>
            <h1 className="mt-6 font-heading uppercase font-semibold tracking-tight text-white text-3xl sm:text-4xl">
              Paiement confirmé
            </h1>
            <p className="mt-5 text-white text-lg font-semibold">Bienvenue dans le Peloton !</p>
            <p className="mt-2 text-white/60">
              Votre adhésion au Peloton des Entreprises du Club Cycliste Le Boulou est confirmée.
            </p>
            <div className="mt-7 border border-white/10 bg-navy p-5 text-left text-sm space-y-2.5">
              <p className="flex justify-between gap-4 text-white/60">
                <span>Formule</span>
                <span className="text-white font-semibold text-right">{data.formule}</span>
              </p>
              <p className="flex justify-between gap-4 text-white/60">
                <span>Montant payé</span>
                <span className="text-white font-semibold text-right">{data.montant}</span>
              </p>
            </div>
            <p className="mt-4 text-white/50 text-xs">Un email de confirmation vous a été envoyé.</p>
          </>
        )}
        {failed && (
          <>
            <Loader2 className="text-white/40 mx-auto" size={36} />
            <h1 className="mt-6 font-heading uppercase font-semibold tracking-tight text-white text-2xl">
              Validation en cours
            </h1>
            <p className="mt-3 text-white/60 text-sm">
              Votre paiement est en cours de validation. Si vous avez bien payé, votre adhésion sera confirmée sous
              quelques minutes par email.
            </p>
          </>
        )}
        <Link
          to="/partenaires"
          className="mt-8 inline-block bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
        >
          Retour au Peloton
        </Link>
      </div>
    </div>
  );
}

export function PaiementAnnule() {
  return (
    <div className="min-h-[75vh] bg-navy flex items-center justify-center px-5 py-32">
      <SEO title="Paiement annulé" />
      <div className="bg-navy2 border border-white/10 p-8 sm:p-12 max-w-xl w-full text-center" data-testid="paiement-annule">
        <span className="mx-auto flex h-14 w-14 items-center justify-center border border-white/20 text-white/70">
          <XCircle size={26} />
        </span>
        <h1 className="mt-6 font-heading uppercase font-semibold tracking-tight text-white text-3xl sm:text-4xl">
          Paiement annulé
        </h1>
        <p className="mt-4 text-white/60">
          Le paiement n'a pas abouti ou a été annulé. Aucun montant n'a été débité. Vous pouvez réessayer ou nous
          contacter pour un autre mode de règlement.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link
            to="/partenaires"
            data-testid="paiement-reessayer"
            className="inline-block bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
          >
            Réessayer
          </Link>
          <Link
            to="/partenaires"
            className="inline-block border border-white/30 text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
          >
            Retour au Peloton
          </Link>
        </div>
      </div>
    </div>
  );
}
